import { Analytics } from '@analytics';
import CloseIcon from '@assets/images/Close.svg';
import FilterIcon from '@assets/images/Filter.svg';
import { Button, Grid, Modal, Toolbar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Menu as MenuIcon } from '@material-ui/icons';
import React, { Fragment, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import TripAdmitFooterMobileIcon from '@assets/images/TripAdmitFooterMobile.svg';
import UserNameIcon from '@assets/images/UserName.svg';
import AppleStoreIcon from '@assets/images/AppStore.png';
import AndroidStoreIcon from '@assets/images/PlayStore.png';
import TipDirectLogoIcon from '@assets/images/tip_direct/TA_Icon.png';
import { appStoreLinks } from '@components/AppPromotion';
import ShareCompanyInviteButton from '@components/ShareCompanyInviteButton';
import { StyleBreakpoints, UserRoles } from '@constants';
import { useEffect } from 'react';
import { isProductEnabled, isPaymentsCompany } from '../../../../utils/helpers';
import { signOutOfTipDirect } from '../../../../utils/signOut';
import InviteGuideModal from '../../../TapToTip/InviteGuideModal';
import AdminJobsButton from '@components/AdminJobs/AdminJobsButton';
import HelpMenu from '../HelpMenu';
import NavLink from '../NavLink';
import PaymentsBadge from '../PaymentsBadge';
import messages from '../messages';
import { NavSurface, TapToTipTabs, formatTabLabel, getMobileMenu } from '../tipDirectTabs';
import { MenuItem } from './menu-item';

const useStyles = makeStyles(theme => ({
  // Strips a native <button> back to the plain box the clickable <div>s here
  // used to be, so making them real buttons (keyboard + screen reader) does not
  // change the look. Declared first so the classes below still win.
  bareButton: {
    display: 'block',
    margin: 0,
    padding: 0,
    border: 0,
    background: 'transparent',
    font: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',
    textAlign: 'inherit',
    cursor: 'pointer'
  },
  // For a button wrapping a lone image: hug the image like the bare <img> did,
  // with no line-box gap under it.
  bareImageButton: {
    display: 'flex'
  },
  mobileHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    width: '100%',
    gridTemplateRows: '1fr'
  },
  logo: {
    justifySelf: 'center'
  },
  filterAndMenu: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    justifySelf: 'flex-end'
  },
  modalBody: {
    position: 'absolute',
    display: 'grid',
    gridTemplateRows: '1fr 6fr 0fr 1.5fr 0fr 0.8fr',
    gridTemplateColumns: '1fr',
    backgroundColor: '#fff',
    width: '70vw',
    minWidth: '250px',
    boxShadow: 'rgb(0 0 0 / 36%) -2px 5px 8px 0px',
    borderRadius: 10,
    top: '2vw',
    right: '2vw',
    bottom: '2vw'
  },
  menuIcon: {
    fontSize: 32
  },
  topModalBlock: {
    display: 'grid',
    gridTemplateColumns: '80% 20%',
    gridTemplateRows: '1fr 10%',
    padding: '16px 16px 0 16px'
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px 14px 8px'
  },
  username: {
    fontWeight: 'bold',
    color: '#000',
    fontSize: 16
  },
  horizontalLine: {
    width: '100%',
    borderBottom: '1px solid #f4f4f4'
  },
  closeBtn: {
    justifySelf: 'flex-end',
    alignSelf: 'center',
    cursor: 'pointer'
  },
  menuItemsContainer: {
    padding: '0 16px 0 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden'
  },
  menuItems: {
    width: '100%',
    overflowY: 'scroll',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  signOut: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    color: '#6997ff',
    flexDirection: 'column',
    padding: '12px 16px 4px',
    cursor: 'pointer'
  },
  footerLogo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: '8px',
    paddingTop: '8px'
  },
  avatarUrl: {
    width: '56px',
    height: '56px',
    objectFit: 'cover',
    objectPosition: '50% 50%',
    borderRadius: '50%'
  },
  userDetails: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '24px'
  },
  companyName: {
    fontWeight: 500,
    color: '#000',
    fontSize: 12
  },
  bottomBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    width: '100%'
  },
  tipDirectLogo: {
    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.24)',
    width: '36px',
    height: '36px',
    borderRadius: '50%'
  },
  appStoreLink: {
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer'
  },
  appStoreImage: {
    width: '100%',
    height: '42px',
    minHeight: '42px',
    [theme.breakpoints.up(StyleBreakpoints.xs)]: {
      height: '72px',
      minHeight: '72px'
    }
  },
  appPromotionContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'center'
  }
}));

export function MobileNavBar({
  username,
  user,
  withFilters = false,
  setIsOpenFilters,
  sendInvitesToGuides = null,
  hasMessagingAlert
}) {
  const classes = useStyles();
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const products = user.companyDetails.products;
  const [isRecommendModalOpen, setIsRecommendModalOpen] = useState(false);
  const intl = useIntl();
  const [mobileOS, setMobileOS] = useState(null);

  useEffect(() => {
    const userAgent = navigator.userAgent;

    if (/android/i.test(userAgent)) {
      setMobileOS('android');
    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      setMobileOS('apple');
    }
  }, []);

  const toggleModal = () => {
    setIsModalOpen(prevState => !prevState);
  };

  const handleSignOut = () => {
    signOutOfTipDirect();
  };

  const handleTapToTipTabClick = ({ name, path }) => {
    history.push({ pathname: path, state: { tab: name } });
    setIsModalOpen(false);
  };

  const userIsPaymentsCompany = isPaymentsCompany(user.companyId, user);

  // Card 354 — every entry, its gate and its order come from the shared
  // definition the desktop rail also renders (../tipDirectTabs). Users is
  // desktopOnly there; Manifest and Bookings are mobileOnly; Messaging sits in
  // the ticketing block when there is one and in the TipDirect list otherwise.
  const mobileMenu = getMobileMenu(user);
  const labelOf = tab => formatTabLabel(intl, tab, user, NavSurface.MOBILE);

  return (
    <Toolbar className={classes.mobileHeader}>
      <div className={classes.notifications}>{/* Notifications Icon will be here */}</div>
      <div className={classes.logo}>
        <NavLink link="/">
          {userIsPaymentsCompany ? (
            <PaymentsBadge compact />
          ) : (
            <img
              className={classes.tipDirectLogo}
              src={TipDirectLogoIcon}
              alt="Tip Direct Mobile Logo"
            />
          )}
        </NavLink>
      </div>
      <div className={classes.filterAndMenu}>
        {withFilters && (
          <button
            type="button"
            className={classes.bareButton}
            aria-label="Filters"
            onClick={setIsOpenFilters}>
            <img src={FilterIcon} alt="" />
          </button>
        )}
        <AdminJobsButton />
        <HelpMenu />
        <button
          type="button"
          className={classes.bareButton}
          data-testid="mobile-menu-button"
          aria-label={isModalOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isModalOpen}
          aria-haspopup="dialog"
          onClick={toggleModal}>
          <MenuIcon color={'primary'} className={classes.menuIcon} />
        </button>
      </div>
      <Modal open={isModalOpen} onClose={toggleModal}>
        <Fragment>
          <Grid className={classes.modalBody} role="dialog" aria-modal="true" aria-label="Menu">
            <div className={classes.topModalBlock}>
              <div className={classes.userInfo}>
                <img
                  className={classes.avatarUrl}
                  src={user.profile?.avatarUrl || UserNameIcon}
                  alt="UserName Icon"
                />
                <div className={classes.userDetails}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {hasMessagingAlert && (
                      <NavLink link="/tipdirect/setup" style={{ minWidth: 'unset', padding: '0', marginRight: '8px' }}>
                        <span
                          style={{
                            width: '10px',
                            height: '10px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            borderRadius: '50%',
                            fontSize: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            flexShrink: 0
                          }}
                        >
                          !
                        </span>
                      </NavLink>
                    )}
                    <span className={classes.username}>{username || user.email}</span>
                  </div>
                  <span className={classes.companyName}>{user.companyName || ''}</span>
                </div>
              </div>
              <button
                type="button"
                className={`${classes.bareButton} ${classes.bareImageButton} ${classes.closeBtn}`}
                aria-label="Close menu"
                onClick={toggleModal}>
                <img src={CloseIcon} alt="" />
              </button>
              <div style={{ gridArea: '2 / 1 / 2 / 3' }} className={classes.horizontalLine} />
            </div>

            <div className={classes.menuItemsContainer}>
              <div className={classes.menuItems}>
                {mobileMenu.ticketing.map(tab => (
                  <MenuItem
                    key={tab.name}
                    title={labelOf(tab)}
                    imgSrc={tab.icons.mobile}
                    link={tab.path}
                    hasAlert={tab.name === TapToTipTabs.MESSAGING ? hasMessagingAlert : undefined}
                  />
                ))}
                {isProductEnabled('TapToTip', products) &&
                  mobileMenu.main.map(tab => {
                    const title = labelOf(tab);
                    return (
                      <div
                        className="flex items-center cursor-pointer w-full border-0 border-b border-solid border-slate-100 pt-4 pb-3 hover:bg-slate-50/75 transition-colors duration-150"
                        key={tab.name}
                        title={title}
                        onClick={() => handleTapToTipTabClick(tab)}>
                        <Button>
                          <img className="w-14 h-14" src={tab.icons.mobile} alt={title} />
                          <span className="ml-6 text-slate-800 font-bold">{title}</span>
                        </Button>
                      </div>
                    );
                  })}
                {mobileMenu.footer.map(tab => (
                  <MenuItem key={tab.name} title={labelOf(tab)} imgSrc={tab.icons.mobile} link={tab.path} />
                ))}
                {/* <MenuItem title="Tip Direct" imgSrc={BookingsIcon} link="/tipdirect" /> */}
              </div>
            </div>
            <div className={classes.horizontalLine} />
            <div className={classes.bottomBlock}>
              {user.role !== UserRoles.paymentsAnalyst && (
                <ShareCompanyInviteButton
                  companyName={user.companyName}
                  inviteCode={user.inviteCode}
                />
              )}
              <div className={classes.appPromotionContainer}>
                {(mobileOS === 'apple' || !mobileOS) && (
                  <button
                    className={classes.appStoreLink}
                    onClick={() => {
                      Analytics.track(`apple app store link clicked`, {
                        page: 'mobile menu'
                      });
                      window.location.replace(appStoreLinks.apple);
                    }}>
                    <img
                      className={classes.appStoreImage}
                      src={AppleStoreIcon}
                      alt={'Apple App Store Icon'}
                    />
                  </button>
                )}
                {(mobileOS === 'android' || !mobileOS) && (
                  <button
                    className={classes.appStoreLink}
                    onClick={() => {
                      Analytics.track(`android app store link clicked`, {
                        page: 'mobile menu'
                      });
                      window.location.replace(appStoreLinks.android);
                    }}>
                    <img
                      className={classes.appStoreImage}
                      src={AndroidStoreIcon}
                      alt={'Google Play Store Icon'}
                    />
                  </button>
                )}
              </div>
              {sendInvitesToGuides && (
                <Button
                  className="!rounded-full !bg-white hover:!bg-slate-50 !text-slate-600 !font-semibold !normal-case !border !border-solid !border-slate-300 !px-4 !py-2 !text-xs w-fit transition-colors"
                  onClick={() => setIsRecommendModalOpen(true)}>
                  {intl.formatMessage(messages.recommendToAFriend)}
                </Button>
              )}

              <button
                type="button"
                className={`${classes.bareButton} ${classes.signOut}`}
                onClick={handleSignOut}>
                <span>Sign out</span>
              </button>
            </div>
            <div className={classes.horizontalLine} />
            <div className={classes.footerLogo}>
              <img src={TripAdmitFooterMobileIcon} alt="TripAdmit Footer Logo" />
            </div>
          </Grid>
        </Fragment>
      </Modal>

      <InviteGuideModal
        isOpen={isRecommendModalOpen}
        setIsOpen={setIsRecommendModalOpen}
        sendInvitesToGuides={sendInvitesToGuides}
        companyName={user.companyName}
        isRecommendationEmail
        inviteCode={user.inviteCode}
      />
    </Toolbar>
  );
}

export default MobileNavBar;
